"use client";
import { useEffect, useState } from 'react';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import { getData } from '../../../funcs/api';
import CourseVideoPlayer from './CourseVideoPlayer';
import CourseContentAccordion from './CourseContentAccordion';
import CourseJoinBlock from './CourseJoinBlock';
import CourseJoinNavButtons from './CourseJoinNavButtons';
import { countTotalEpisodes, getCourseSections, getInstructorDisplayName } from '../lib/courseSections';
import { getLessonLearnedItems } from '../lib/lessonLearned';
import { normalizeCourseVideoStyle } from '../lib/courseVideoStyle';
import { fetchCourseAcceptedSubscriberCount } from '../lib/courseLearnerApi';
import styles from '../[id]/course-details.module.scss';

// Static placeholders (ratings until wired to API)
const PLACEHOLDERS = {
    rating: 4.8,
    reviewsCount: 210,
};

export default function CourseDetailClientFallback({ courseId }) {
    const params = useParams();
    const id = courseId || params?.id;
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [acceptedSubscribers, setAcceptedSubscribers] = useState(null);

    useEffect(() => {
        if (!id) return;
        let cancelled = false;
        fetchCourseAcceptedSubscriberCount(id).then((n) => {
            if (!cancelled) setAcceptedSubscribers(n);
        });
        return () => {
            cancelled = true;
        };
    }, [id]);

    useEffect(() => {
        async function fetchCourse() {
            if (!id) {
                setError('Course ID is missing');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const data = await getData(`course/${id}`, '_embed');
                
                if (!data || !data.id) {
                    setError('Course not found');
                    setLoading(false);
                    return;
                }
                
                setCourse(data);
                setError(null);
            } catch (err) {
                console.error('Client-side fetch error:', err);
                setError(err);
                setCourse(null);
            } finally {
                setLoading(false);
            }
        }

        fetchCourse();
    }, [id]);

    if (loading) {
        return (
            <main className={styles.courseDetailsPage}>
                <div className="container py-5">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">جار التحميل...</span>
                        </div>
                        <p className="text-muted mt-3">جار تحميل بيانات الدورة...</p>
                    </div>
                </div>
            </main>
        );
    }

    if (error || !course) {
        return (
            <main className={styles.courseDetailsPage}>
                <div className="container py-5">
                    <div className="alert alert-warning text-center" role="alert">
                        <h4>الدورة غير موجودة</h4>
                        <p className="mb-3">عذراً، الدورة التي تبحث عنها غير موجودة أو تم حذفها.</p>
                        <Link href="/services/courses" className="btn btn-primary">
                            العودة لقائمة الدورات
                        </Link>
                    </div>
                </div>
            </main>
        );
    }

    const title = course.title?.rendered || "بدون عنوان";
    const level = course.acf?.level || "غير محدد";
    const duration = course.acf?.duration || "غير محدد";
    const instructor = getInstructorDisplayName(course);
    const isOpen = course.acf?.open !== "no" && course.acf?.open !== false;
    const courseSections = getCourseSections(course);
    const totalLessonVideos = countTotalEpisodes(courseSections);
    const introFallback =
        typeof course.acf?.section_intro?.episod_1 === "string"
            ? course.acf.section_intro.episod_1.trim()
            : "";
    const videoMode = normalizeCourseVideoStyle(course.acf?.video_style);
    const whatYouWillLearn = getLessonLearnedItems(course);

    return (
        <main className={styles.courseDetailsPage}>
            <div className={styles.hero}>
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-7 mb-4 mb-lg-0">
                            <span className="badge bg-primary mb-3 px-3 py-2 rounded-pill">{level}</span>
                            <h1>{title}</h1>
                            <div className={styles.meta}>
                                <span><i className="bi bi-person-circle"></i> {instructor}</span>
                                <span><i className="bi bi-clock"></i> {duration}</span>
                                {/* <span><i className="bi bi-star-fill text-warning"></i> {PLACEHOLDERS.rating} ({PLACEHOLDERS.reviewsCount} تقييم)</span> */}
                                <span>
                                    <i className="bi bi-people"></i>{' '}
                                    {acceptedSubscribers !== null
                                        ? `${acceptedSubscribers.toLocaleString('ar-EG')} مشترك`
                                        : '—'}
                                </span>
                            </div>
                            <div className="d-flex gap-3">
                                <CourseJoinNavButtons courseId={id} isOpen={isOpen} variant="hero" />
                                <button className="btn btn-outline-dark btn-lg rounded-pill"><i className="bi bi-share"></i> مشاركة</button>
                            </div>
                            <div id="course-enrollment" className={styles.enrollmentAnchor}>
                                <CourseJoinBlock courseId={id} />
                            </div>
                        </div>
                        <div className="col-lg-5">
                            <div
                                className={`${styles.videoWrapper} ${videoMode === "portrait" ? styles.videoWrapperPortrait : ""}`.trim()}
                            >
                                <CourseVideoPlayer
                                    course={course}
                                    title={title}
                                    fallbackVideoUrl={introFallback}
                                    videoStyle={course.acf?.video_style}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container py-5">
                <div className="row">
                    <div className="col-lg-8">
                        {course.content?.rendered && (
                            <section className="mb-5 bg-white p-4 rounded-4 shadow-sm border">
                                <h3 className={styles.sectionTitle}>محتوى الدورة بالتفصيل</h3>
                                <div dangerouslySetInnerHTML={{ __html: course.content.rendered }} />
                            </section>
                        )}

                        {whatYouWillLearn.length > 0 && (
                            <section className="mb-5 bg-white p-4 rounded-4 shadow-sm border">
                                <h3 className={styles.sectionTitle}>ماذا ستتعلم في هذه الدورة؟</h3>
                                <ul className={`row row-cols-1 row-cols-md-2 ${styles.checkList}`}>
                                    {whatYouWillLearn.map((item, index) => (
                                        <li key={`${index}-${item.slice(0, 24)}`} className="col text-muted">
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        <section id="course-curriculum" className={`mb-5 ${styles.courseCurriculumAnchor}`}>
                            <h3 className={styles.sectionTitle}>محتوى الدورة</h3>
                            {courseSections.length === 0 ? (
                                <p className="text-muted mb-0">لا يوجد محتوى مسجل لهذه الدورة بعد.</p>
                            ) : (
                                <CourseContentAccordion
                                    courseId={id}
                                    sections={courseSections}
                                    videoStyle={course.acf?.video_style}
                                    accordionId="courseAccordionFallback"
                                />
                            )}
                        </section>
                    </div>

                    <div className="col-lg-4">
                        <div className={styles.sidebarCard}>
                            <span className={styles.price}>مجاني</span>
                            <CourseJoinNavButtons courseId={id} isOpen={isOpen} variant="sidebar" sidebarTone="success" />
                            <p className="text-center text-muted small mb-0">ضمان استرداد الأموال لمدة 30 يوم</p>
                            <ul className={styles.stats}>
                                <li><span><i className="bi bi-clock-history"></i> مدة الدورة</span><strong>{duration}</strong></li>
                                <li><span><i className="bi bi-bar-chart"></i> المستوى</span><strong>{level}</strong></li>
                                <li><span><i className="bi bi-journal-text"></i> الدروس</span><strong>{totalLessonVideos} فيديو</strong></li>
                                <li><span><i className="bi bi-globe"></i> اللغة</span><strong>العربية</strong></li>
                                <li><span><i className="bi bi-award"></i> شهادة إتمام</span><strong>نعم</strong></li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
}
